import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import {
  fetchPortfolio,
  fetchAIRecommendations,
  fetchRecentTrades,
  fetchStockQuote,
  fetchOrderHistory,
  placeOrder,
  cancelOrder,
} from '../../../services/stockai/api/client';
import { getMarketStatus } from '../../../services/stockai/marketStatus';
import {
  formatKrw,
  getNaverFinanceUrl,
  loadRecentStocks,
  normalizeSelectedStock,
  saveRecentStock,
} from '../../../services/stockai/stockUtils';
import StockSearchField from '../components/StockSearchField';

function statusLabel(status) {
  if (status === 'completed' || status === '주문완료' || status === 'filled') return { text: '체결', tone: 'ok' };
  if (status === 'cancelled' || status === '취소') return { text: '취소', tone: 'muted' };
  if (status === 'failed' || status === '실패') return { text: '실패', tone: 'warn' };
  return { text: '대기', tone: 'pending' };
}

export default function Trade() {
  const { loggedIn, mock, openLogin, authTick } = useOutletContext() || {};
  const location = useLocation();
  const incoming = location.state?.selectedStock;

  const [selected, setSelected] = useState(() =>
    incoming ? normalizeSelectedStock(incoming, incoming.source) : normalizeSelectedStock({ symbol: '005930', name: '삼성전자', price: 0 }, 'search')
  );
  const [quickPortfolio, setQuickPortfolio] = useState([]);
  const [quickRecs, setQuickRecs] = useState([]);
  const [recentStocks, setRecentStocks] = useState(() => loadRecentStocks());
  const [orders, setOrders] = useState([]);
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [orderType, setOrderType] = useState(incoming?.orderType || 'buy');
  const [orderMethod, setOrderMethod] = useState('limit');
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [market, setMarket] = useState(() => getMarketStatus());

  useEffect(() => {
    setMarket(getMarketStatus());
    const id = setInterval(() => setMarket(getMarketStatus()), 30000);
    return () => clearInterval(id);
  }, []);

  const loadOrders = useCallback(async () => {
    if (!mock && !loggedIn) {
      setOrders([]);
      return;
    }
    try {
      const data = await fetchOrderHistory({ days: 7, limit: 30 });
      setOrders(data.orders || []);
    } catch (err) {
      console.warn('[StockAI] 주문 내역 로드 실패:', err.message);
    }
  }, [mock, loggedIn]);

  useEffect(() => {
    if (incoming) {
      setSelected(normalizeSelectedStock(incoming, incoming.source));
      setOrderType(incoming.orderType || 'buy');
      if (incoming.currentPrice) setPrice(String(incoming.currentPrice));
    }
  }, [incoming]);

  // 초기/로그인 후 실 시세 반영 (삼성전자 기본 종목 포함)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selected?.symbol) return;
      if (!mock && !loggedIn) return;
      const quote = await fetchStockQuote(selected.symbol);
      if (cancelled || !quote?.price) return;
      setSelected((prev) => ({
        ...prev,
        currentPrice: quote.price,
        name: quote.name || prev.name,
      }));
      // 로그인·종목 변경 시 실시세로 지정가 동기화 (이후 사용자가 수정 가능)
      setPrice(String(quote.price));
    })();
    return () => { cancelled = true; };
    // authTick: 로그인 직후 목업→실시세 재조회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.symbol, loggedIn, mock, authTick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mock || loggedIn) {
        const [p, r, t] = await Promise.all([
          fetchPortfolio(),
          fetchAIRecommendations(),
          fetchRecentTrades(),
        ]);
        if (cancelled) return;
        setQuickPortfolio((p.portfolio || []).map((h) => normalizeSelectedStock(h, 'portfolio')));
        setQuickRecs((r.recommendations || []).map((rec) => normalizeSelectedStock(rec, 'recommendation')));
        const fromTrades = (t.trades || []).map((tr) =>
          normalizeSelectedStock(
            { symbol: tr.symbol, name: tr.stock_name, price: tr.price },
            'recent'
          )
        );
        setRecentStocks((prev) => {
          const merged = [...fromTrades, ...prev];
          const seen = new Set();
          return merged.filter((s) => {
            if (seen.has(s.symbol)) return false;
            seen.add(s.symbol);
            return true;
          }).slice(0, 8);
        });
        await loadOrders();
      } else {
        setOrders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [loggedIn, mock, authTick, loadOrders]);

  const handleSelectStock = async (stock) => {
    const normalized = normalizeSelectedStock(stock, stock.source || 'search');
    setSelected(normalized);
    setRecentStocks((prev) => {
      const next = [normalized, ...prev.filter((s) => s.symbol !== normalized.symbol)].slice(0, 8);
      saveRecentStock(normalized);
      return next;
    });
    const quote = await fetchStockQuote(normalized.symbol);
    if (quote?.price) {
      setSelected((prev) => ({ ...prev, currentPrice: quote.price, name: quote.name || prev.name }));
      setPrice(String(quote.price));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!mock && !loggedIn) {
      openLogin?.();
      return;
    }

    setLoading(true);
    try {
      const result = await placeOrder({
        symbol: selected.symbol,
        order_type: orderType,
        quantity: Number(quantity),
        price: price ? Number(price) : undefined,
        order_method: orderMethod,
      });
      const status = result.status || 'pending';
      setMessage(
        result.message
          || `${orderType === 'buy' ? '매수' : '매도'} 주문이 ${status === 'pending' ? '대기' : '접수'}되었습니다.`
      );
      saveRecentStock({ ...selected, source: 'recent' });
      await loadOrders();
    } catch (err) {
      setError(err.message || '주문 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId) => {
    if (!orderId) return;
    if (!window.confirm('대기 중인 주문을 취소하시겠습니까?')) return;
    setCancellingId(orderId);
    setError('');
    try {
      const result = await cancelOrder(orderId);
      setMessage(result.message || '주문이 취소되었습니다.');
      await loadOrders();
    } catch (err) {
      setError(err.message || '주문 취소 실패');
    } finally {
      setCancellingId(null);
    }
  };

  const pendingOrders = orders.filter((o) => {
    const s = (o.status || '').toLowerCase();
    return s === 'pending' || s === '대기' || s === 'open' || s === 'submitted';
  });

  const renderQuickPick = (title, items) => {
    if (!items.length) return null;
    return (
      <div className="stockai-quickpick">
        <div className="stockai-stat-label" style={{ marginBottom: '0.5rem' }}>{title}</div>
        <div className="stockai-chip-list">
          {items.map((item) => (
            <button
              key={`${item.source}-${item.symbol}`}
              type="button"
              className="stockai-chip stockai-chip--button"
              onClick={() => handleSelectStock(item)}
            >
              <strong>{item.symbol}</strong> {item.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="stockai-kicker">Execution Console</div>
      <div className="stockai-page-title-row">
        <h1 className="stockai-page-title">거래</h1>
        <div
          className={`stockai-market-badge ${market.open ? 'stockai-market-badge--open' : 'stockai-market-badge--closed'}`}
          title={market.detail}
        >
          <span className="stockai-market-badge__dot" aria-hidden="true" />
          {market.label}
          <small>{market.detail}</small>
        </div>
      </div>
      {!mock && !loggedIn && (
        <div className="stockai-alert">
          로그인 후 실제 시세·주문이 가능합니다.{' '}
          <button type="button" className="stockai-btn stockai-btn--sm" onClick={openLogin}>로그인</button>
        </div>
      )}
      <div className="stockai-trade-grid">
        <div className="stockai-card">
          <div className="stockai-section__header">
            <div>
              <h2 className="stockai-section__title">Order Ticket</h2>
              <div className="stockai-section__subtitle">종목명·코드 통합 검색 후 주문합니다.</div>
            </div>
            <span className="stockai-badge">{mock ? 'Paper Flow' : 'Live Order'}</span>
          </div>

          <StockSearchField selected={selected} onSelect={handleSelectStock} />

          {renderQuickPick('포트폴리오', quickPortfolio)}
          {renderQuickPick('AI 추천', quickRecs)}
          {renderQuickPick('최근 조회', recentStocks)}

          <form onSubmit={handleSubmit}>
            <div className="stockai-form-grid">
              <div className="stockai-form-field">
                <label className="stockai-form-label">주문 유형</label>
                <select className="stockai-select" value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                  <option value="buy">매수</option>
                  <option value="sell">매도</option>
                </select>
              </div>
              <div className="stockai-form-field">
                <label className="stockai-form-label">주문 방식</label>
                <select className="stockai-select" value={orderMethod} onChange={(e) => setOrderMethod(e.target.value)}>
                  <option value="limit">지정가</option>
                  <option value="market">시장가</option>
                </select>
              </div>
              <div className="stockai-form-field stockai-form-field--full">
                <label className="stockai-form-label">선택 종목</label>
                <div className="stockai-selected-stock">
                  <strong>{selected.name}</strong>
                  <span className="stockai-stat-label"> ({selected.symbol})</span>
                  {selected.currentPrice > 0 && (
                    <span className="stockai-stat-label"> · {formatKrw(selected.currentPrice)}</span>
                  )}
                </div>
              </div>
              <div className="stockai-form-field">
                <label className="stockai-form-label">수량</label>
                <input
                  className="stockai-input"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                />
              </div>
              {orderMethod === 'limit' && (
                <div className="stockai-form-field">
                  <label className="stockai-form-label">가격 (원)</label>
                  <input
                    className="stockai-input"
                    type="number"
                    min="1"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="지정가"
                    required
                  />
                </div>
              )}
            </div>

            {message && <div className="stockai-alert stockai-alert--info">{message}</div>}
            {error && <div className="stockai-alert">{error}</div>}

            <button type="submit" className="stockai-btn" disabled={loading} style={{ width: '100%', marginTop: '0.5rem' }}>
              {loading ? '주문 중...' : mock ? '주문 (Mock)' : '주문 실행'}
            </button>
          </form>
        </div>

        <div className="stockai-hero__aside">
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">선택 종목</span>
              <strong className="stockai-metric__value">{selected.symbol}</strong>
              <span className="stockai-metric__meta">
                {selected.name} / {orderType === 'buy' ? '매수' : '매도'}
                {selected.currentPrice > 0 ? ` · ${formatKrw(selected.currentPrice)}` : ''}
              </span>
            </div>
          </div>

          <div className="stockai-card">
            <div className="stockai-section__header" style={{ marginBottom: '0.75rem' }}>
              <h3 className="stockai-card__title" style={{ margin: 0 }}>매수·매도 대기</h3>
              <span className="stockai-badge">{pendingOrders.length}건</span>
            </div>
            {pendingOrders.length === 0 ? (
              <p className="stockai-stat-label">대기 중인 주문이 없습니다.</p>
            ) : (
              <ul className="stockai-order-list">
                {pendingOrders.map((order) => {
                  const st = statusLabel(order.status);
                  return (
                    <li key={order.id || `${order.symbol}-${order.created_at}`} className="stockai-order-item">
                      <div className="stockai-order-item__main">
                        <strong>
                          {order.order_type === 'sell' || order.order_type === '매도' ? '매도' : '매수'}{' '}
                          {order.stock_name || order.symbol}
                        </strong>
                        <span className="stockai-stat-label">
                          {order.quantity}주 · {formatKrw(order.price)}
                        </span>
                        <span className={`stockai-order-status stockai-order-status--${st.tone}`}>
                          {st.text}
                        </span>
                      </div>
                      {order.id && (
                        <button
                          type="button"
                          className="stockai-btn stockai-btn--ghost stockai-btn--sm stockai-btn--danger"
                          disabled={cancellingId === order.id}
                          onClick={() => handleCancel(order.id)}
                        >
                          {cancellingId === order.id ? '취소 중...' : '취소'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {selected.reason && (
            <div className="stockai-card">
              <h3 className="stockai-card__title">AI 분석 요약</h3>
              <p className="stockai-stat-label">{selected.reason}</p>
              {selected.confidence != null && (
                <span className="stockai-badge">신뢰도 {selected.confidence}%</span>
              )}
            </div>
          )}
          <div className="stockai-card">
            <h3 className="stockai-card__title">외부 상세 정보</h3>
            <p className="stockai-stat-label">네이버 증권에서 차트·뉴스·시세를 확인할 수 있습니다.</p>
            <a
              className="stockai-btn stockai-btn--ghost"
              href={getNaverFinanceUrl(selected.symbol)}
              target="_blank"
              rel="noopener noreferrer"
            >
              네이버 증권 열기
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
